import { Client } from '@microsoft/microsoft-graph-client';
import { GRAPH_CLIENT_MIDDLEWARE_OPTIONS } from './GraphClientMiddlewareOptions'
import { LogEvents } from '../../utils/LogEvents';
import { ErrorStringFromException, isEmptyString, isNullOrUndefined } from '../../utils/utils';

const MAX_BYTES_TO_UPLOAD_WITH_SESSION_URL : number = 10 * 1024 * 1024; // 10 MB

/**
 * API to fetch all my drives from OneDrive ( OD )
 * @param graphClient One Drive Graph Client
 * @returns All OD drives in array
 */
export async function GraphGetMyDrives( graphClient : Client ){
    checkObjectNullOrUndefined(graphClient, "GraphGetMyDrives", "graphClient");
    return callApiWithTryCatch(
        () => getRequest(graphClient, "/me/drives"),
        "GraphGetMyDrives"
    );
}

/**
 * API to fetch drive item object from OD using folder path and file nmae
 * @param graphClient One Drive Graph Client
 * @param driveId Drive Id
 * @param folderPath Folder path from root
 * @param fileName File name to fetch
 * @param commaSeparatedProperties Specific properties to fetch instead of whole object
 * @returns Drive item object
 */
export async function GraphGetOneDriveItemByFileNameRequest(graphClient: Client, driveId: string, folderPath:string, fileName: string, commaSeparatedProperties : string = "") {
    checkObjectNullOrUndefined(graphClient, "GraphGetOneDriveItemByFileNameRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphGetOneDriveItemByFileNameRequest", "driveId");
    checkStringNullUndefinedOrEmpty(folderPath, "GraphGetOneDriveItemByFileNameRequest", "folderPath");
    checkStringNullUndefinedOrEmpty(fileName, "GraphGetOneDriveItemByFileNameRequest", "fileName");

    return callApiWithTryCatch(
        () => getRequest(graphClient, "/drives/" + 
        encodeURIComponent(driveId) + "/root:/" + 
        encodeURIComponent(folderPath) + "/" + 
        encodeURIComponent(fileName), commaSeparatedProperties), 
        "GraphGetOneDriveItemByFileNameRequest"
    );
}

/**
 * API to fetch drive item from OD using item id
 * @param graphClient One Drive Graph Client
 * @param driveId Drive Id
 * @param itemId ID of item to fetch
 * @param commaSeparatedProperties Specific properties to fetch instead of whole object
 * @returns Drive item object
 */
export async function GraphGetOneDriveItemByIdRequest(graphClient: Client, driveId: string, itemId: string, commaSeparatedProperties : string = "") {
    checkObjectNullOrUndefined(graphClient, "GraphGetOneDriveItemByIdRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphGetOneDriveItemByIdRequest", "driveId");
    checkStringNullUndefinedOrEmpty(itemId, "GraphGetOneDriveItemByIdRequest", "itemId");
    return callApiWithTryCatch(
        () => getRequest(graphClient, `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}`, commaSeparatedProperties), 
        "GraphGetOneDriveItemByIdRequest"
    );
}

/**
 * API to fetch children of an item
 * @param graphClient One Drive Graph Client
 * @param driveId Drive Id
 * @param itemId Item Id
 * @returns Children of item
 */
export async function GraphGetOneDriveItemChildrenRequest(graphClient: Client, driveId: string, itemId: string) {
    checkObjectNullOrUndefined(graphClient, "GraphGetOneDriveItemChildrenRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphGetOneDriveItemChildrenRequest", "driveId");
    checkStringNullUndefinedOrEmpty(itemId, "GraphGetOneDriveItemChildrenRequest", "itemId");
    return callApiWithTryCatch(
        () => getRequest(graphClient, `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/children`),
        "GraphGetOneDriveItemChildrenRequest"
    );
}

/**
 * API to delete drive item 
 * @param graphClient One Drive Graph Client
 * @param driveId Drive Id
 * @param itemId Item id to delete
 * @returns Success status on deletion
 */
export async function GraphDeleteOneDriveItemRequest(graphClient: Client, driveId: string, itemId: string) {
    checkObjectNullOrUndefined(graphClient, "GraphDeleteOneDriveItemRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphDeleteOneDriveItemRequest", "driveId");
    checkStringNullUndefinedOrEmpty(itemId, "GraphDeleteOneDriveItemRequest", "itemId");
    return callApiWithTryCatch(
        () => deleteRequest(graphClient, `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}`),
        "GraphDeleteOneDriveItemRequest"
    );
}

/**
 * API to rename drive item
 * @param graphClient One Drive Graph Client
 * @param driveId Drive id
 * @param driveItemId Drive item Id to rename
 * @param itemNewName New name
 * @returns Drive item object with updated name
 */
export async function GraphRenameDriveItemRequest(graphClient: Client, driveId: string, driveItemId: string, itemNewName: string) {
    checkObjectNullOrUndefined(graphClient, "GraphRenameDriveItemRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphRenameDriveItemRequest", "driveId");
    checkStringNullUndefinedOrEmpty(itemNewName, "GraphRenameDriveItemRequest", "itemNewName");
    return callApiWithTryCatch(
        () => {
            const renameBody = {
                "name": itemNewName
            };
            const headers : Record<string,string> =  {
                "Content-type" : "application/json"
            }
            return patchRequest(graphClient, `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(driveItemId)}`, headers, renameBody);
        },
        "GraphRenameDriveItemRequest"
    );
}

/**
 * API to fetch drive item content
 * @param graphClient One Drive Graph Client
 * @param driveId Drive ID
 * @param itemId Item id for which content should be fetched
 * @returns Content of drive item 
 */
export async function GraphGetOneDriveItemContentRequest(graphClient: Client, driveId: string, itemId: string) {
    checkObjectNullOrUndefined(graphClient, "GraphGetOneDriveItemContentRequest", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphGetOneDriveItemContentRequest", "driveId");
    checkStringNullUndefinedOrEmpty(itemId, "GraphGetOneDriveItemContentRequest", "itemId");
    return callApiWithTryCatch(
        async () => {
            const response = await getRequest(graphClient, `/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}?select=id,@microsoft.graph.downloadUrl`);
            return httpGetRequest(response['@microsoft.graph.downloadUrl'], "", {});
        },
        "GraphGetOneDriveItemContentRequest"
    );
}

/**
 * API to create upload session, this session will later be used for uploading file to OD
 * @param graphClient One Drive Graph Client
 * @param folderPath Folder path starting from root
 * @param fileName File name for which upload session should be created
 * @param driveItemMetaData Metadata to be added at the time of fle creation
 * @returns Upload URL to be used as path to upload file content
 */
export async function GraphCreateFileUploadSession(
    graphClient: Client,
    folderPath : string,
    fileName: string,
    driveItemMetaData: string = "{}",
  ): Promise<any> {
    checkObjectNullOrUndefined(graphClient, "GraphCreateFileUploadSession", "graphClient");
    checkValidJsonString(driveItemMetaData, "GraphCreateFileUploadSession");
    checkStringNullUndefinedOrEmpty(folderPath, "GraphCreateFileUploadSession", "folderPath");
    checkStringNullUndefinedOrEmpty(fileName, "GraphCreateFileUploadSession", "fileName");

    const metaData = Buffer.from(
        driveItemMetaData
    );
    // TODO : Explore drive ID based URL instead of /me/drive 
    const metaDataBuffer = Buffer.from(metaData);
    const url = '/me/drive/root:/' + 
    encodeURIComponent(folderPath) + '/' + 
    encodeURIComponent(fileName) + ':/createUploadSession';
    return callApiWithTryCatch(
        () => postRequest(graphClient, url, metaDataBuffer, {}),
        "GraphCreateFileUploadSession"
        );
  }

  /**
   * API to upload file/content 
   * @param graphClient One Drive Graph Client
   * @param uploadUrl Upload URL to be fetched using GraphCreateFileUploadSession method
   * @param fileContent File content to upload
   * @returns Array of chunk upload responses, chunk size is 10 MB, see MAX_BYTES_TO_UPLOAD_WITH_SESSION_URL
   */
  export async function GraphUploadFileWithSessionUrl(
    graphClient: Client,
    uploadUrl: string,
    fileContent: string,
  ): Promise<any> {
    checkObjectNullOrUndefined(graphClient, "GraphUploadFileWithSessionUrl", "graphClient");
    checkStringNullUndefinedOrEmpty(uploadUrl, "GraphUploadFileWithSessionUrl", "uploadUrl");
    checkStringNullUndefinedOrEmpty(fileContent, "GraphUploadFileWithSessionUrl", "fileContent");

    const fileContentBuffer = Buffer.from(fileContent);
    const byteLength = fileContentBuffer.byteLength;
    let totalBytesSent = 0;
    const response = [];
    
    while(totalBytesSent < byteLength){
        const start = totalBytesSent;
        const end = Math.min(totalBytesSent + MAX_BYTES_TO_UPLOAD_WITH_SESSION_URL-1, byteLength-1);
        const contentRange = 'bytes ' + start + '-' + end + '/' + byteLength;
        const headers : Record<string,string> = {
            'Content-Length' : JSON.stringify(Math.min(MAX_BYTES_TO_UPLOAD_WITH_SESSION_URL, end-start+1)),
            'Content-Range' : contentRange
        };
       
        const buff = new Int8Array(fileContentBuffer.slice(start, end+1));
        response.push(await putRequest(graphClient, uploadUrl, buff, headers));
        totalBytesSent = end+1;
    }
    return response;
}

/**
 * 
 * @param graphClient One Drive Graph Client
 * @param driveId Drive Id
 * @param itemId Item Id
 * @param content New content to update existing drive item, it will overlap on previous data, NOT append
 * @returns Success API response
 */
export async function GraphUpdateDriveItemContent(
        graphClient : Client, 
        driveId : string,
        itemId : string,
        content : string
    ) {
    checkObjectNullOrUndefined(graphClient, "GraphUpdateDriveItemContent", "graphClient");
    checkStringNullUndefinedOrEmpty(driveId, "GraphUpdateDriveItemContent", "driveId");
    checkStringNullUndefinedOrEmpty(itemId, "GraphUpdateDriveItemContent", "itemId");
    const headers : Record<string,string> = {
        "Content-Type" : "text/plain"
    }
    return callApiWithTryCatch(
        () => putRequest(graphClient, "/drives/" + 
        encodeURIComponent(driveId) + "/items/" + 
        encodeURIComponent(itemId) + "/content", content, headers),
        "GraphUpdateDriveItemContent"
    );
}


/*
=============================================
============ Local/Internal Methods =========
=============================================
*/

/*make get request to one drive via graph* */
async function getRequest(graphClient: Client, path: string, commaSeparatedProperties : string = "") {
    if(isEmptyString(commaSeparatedProperties)){
        return  graphClient.api(path)
        .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
        .get();
    } 
    return graphClient.api(path)
    .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
    .query({select: commaSeparatedProperties})
    .get();
}

/*make delete request to one drive via graph* */
async function deleteRequest(graphClient: Client, path: string) {
    return graphClient.api(path)
          .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
          .delete();
}

/*make patch rename request to one drive via graph* */
async function patchRequest(graphClient: Client, path: string, body : any, headers : Record<string,string> = {}) {
    return graphClient.api(path)
          .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
          .headers(headers)
          .patch(body);
}

/*make patch rename request to one drive via graph* */
async function postRequest(graphClient: Client, path: string, body : any, headers : Record<string,string> = {}) {
    return graphClient.api(path)
          .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
          .headers(headers)
          .post(body)
}

/*make patch rename request to one drive via graph* */
async function putRequest(graphClient: Client, path: string, body : any, headers : Record<string,string> = {}) {
    
    checkStringNullUndefinedOrEmpty(path, "putRequest", "path");

    return graphClient.api(path)
          .middlewareOptions(GRAPH_CLIENT_MIDDLEWARE_OPTIONS)
          .headers(headers)
          .put(body)
}

async function httpGetRequest(path : string, commaSeparatedProperties : string = "", headers : Record<string,string> = {}){
    checkStringNullUndefinedOrEmpty(path, "httpGetRequest", "path");

    return new Promise<any>(function(resolve, reject){
        const req = new XMLHttpRequest();
        if(isEmptyString(commaSeparatedProperties)){
            req.open('GET',path);
        } else {
            req.open('GET',path + "?select="+commaSeparatedProperties);
        }
        
        for(const headerKey in headers){
            req.setRequestHeader(headerKey, headers[headerKey]);
        }
        
        req.onload = function(){
            resolve(this.responseText);
        }
        req.onerror = function () {
            reject(req);
        };
        req.send();
    });
}

function checkObjectNullOrUndefined(object : any, method : string, param : string) {
    if(isNullOrUndefined(object)){
        throw new Error("Param '"+ param +"' in Graph API call is Null or Undefined in method '" + method + "'");
    }
}

function checkStringNullUndefinedOrEmpty(path : string, method : string, param : string){
    if(isEmptyString(path)){
        throw new Error("Param '" + param + "' in Graph api method '" + method + "' is Null, Undefined or Empty.");
    }
}

function checkValidJsonString(jsonString : string, method : string){
    try {
        JSON.parse(jsonString);
    }
    catch (error:any) {
        throw new Error("Invalid JSON in Graph API method '" + method + "' : " + ErrorStringFromException(error));
    }
}

async function callApiWithTryCatch(apiCall : ()=>Promise<any>, apiName : string){
    LogEvents.ServiceGraphOneDriveItem.Start();
    try {
        const response = await apiCall();
        LogEvents.ServiceGraphOneDriveItem.AddResponse({ 
            isSuccessful: response != null, 
            message: getResponseMessage(response, apiName), 
            isFinalResponse: true 
        });
        return response;
    } catch (e) {
        LogEvents.ServiceGraphOneDriveItem.AddResponse({ 
            isSuccessful: false, 
            message: "Error caught while executing " + apiName + " : " + ErrorStringFromException(e), 
            isFinalResponse: true 
        });
        throw e;
    }
}

function getNullResponseErrorMessage(apiName : string){
    return "Error: Response recieved for " + apiName + " was NULL";
}

function getNonNullResponseSuccessMessage(apiName : string){
    return "Success : Received Non-Null response for " + apiName;
}

function getResponseMessage(response : any, apiName : string){
    return response == null ? getNullResponseErrorMessage(apiName) : getNonNullResponseSuccessMessage(apiName);
}